import { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { theme, rgba } from '../../theme';

// Generic runtime view for any AI-generated module - reads its own shape (fields,
// apiPath, endpoint) from the module's saved specs rather than needing a per-module
// hand-written or generated component. Talks to the module's real, live, tenant-scoped
// API mounted by backend/services/dynamicModuleRouter.js - this is the actual data,
// not a mock.
// Same ranks as backend/middleware/requireRole.js. The backend's dynamicModuleRouter
// refuses module writes below 'manager' with a 403; this only hides the controls that
// would hit it. Reading the list stays available to everyone.
const ROLE_RANK = { viewer: 0, analyst: 1, employee: 1, manager: 2, admin: 3, superadmin: 4 };
const WRITE_MIN_ROLE = 'manager';

export default function ModuleRuntimeView({ module, onOpenChat, onBack }) {
  const { user } = useAuth();
  const companyId = user?.businessId;
  const specs = module.specs || {};
  const { apiPath, endpoint, fields = [], dataKey, itemName } = specs;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const canRun = !!(companyId && apiPath && endpoint);
  const canWrite = (ROLE_RANK[user?.role] ?? -1) >= ROLE_RANK[WRITE_MIN_ROLE];

  const load = async () => {
    if (!canRun) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.moduleRequest(companyId, apiPath, `/${endpoint}`);
      setItems(data[dataKey] || []);
    } catch (err) {
      setError(err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module.id]);

  const emptyForm = () => fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {});

  const startAdd = () => { setFormData(emptyForm()); setShowAddForm(true); };

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusyId('new');
    try {
      await apiService.moduleRequest(companyId, apiPath, `/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      setShowAddForm(false);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to add.');
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (item) => { setEditingId(item._id); setEditData({ ...item }); };
  const cancelEdit = () => setEditingId(null);

  const submitEdit = async (id) => {
    setBusyId(id);
    try {
      await apiService.moduleRequest(companyId, apiPath, `/${endpoint}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(editData)
      });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update.');
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (id) => {
    setBusyId(id);
    try {
      await apiService.moduleRequest(companyId, apiPath, `/${endpoint}/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete.');
    } finally {
      setBusyId(null);
    }
  };

  const askToConnect = () => {
    onOpenChat?.(`I'm working on the "${module.name}" module. Please connect this module to MongoDB. Create the necessary schema/model and update the routes to use the database instead of mock data.`);
  };

  const fieldInput = (field, value, onChange) => {
    if (field.type === 'select') {
      return (
        <select value={value ?? ''} onChange={e => onChange(e.target.value)} style={styles.input}>
          <option value="">-</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    const type = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
    return (
      <input
        type={type}
        value={value ?? ''}
        placeholder={field.placeholder || field.name}
        onChange={e => onChange(e.target.value)}
        style={styles.input}
      />
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{module.name}</h1>
          <p style={styles.sub}>{module.description}</p>
        </div>
        {canRun && canWrite && (
          <button style={styles.addBtn} onClick={startAdd}>
            <Plus size={16} /> Add {itemName || 'item'}
          </button>
        )}
      </div>

      {!canRun && (
        <div style={styles.notice}>
          This module doesn't have the routing info needed to run yet.
        </div>
      )}

      {error && (
        <div style={styles.errorBox}>
          <p style={{ margin: 0 }}>{error}</p>
          <button style={styles.linkBtn} onClick={askToConnect}>Ask Aetheris to connect this module to its database</button>
        </div>
      )}

      {canWrite && showAddForm && (
        <form onSubmit={submitAdd} style={styles.formCard}>
          {fields.map(f => (
            <div key={f.name} style={styles.formRow}>
              <label style={styles.label}>{f.placeholder || f.name}{f.required ? ' *' : ''}</label>
              {fieldInput(f, formData[f.name], v => setFormData({ ...formData, [f.name]: v }))}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" disabled={busyId === 'new'} style={styles.saveBtn}>Save</button>
            <button type="button" onClick={() => setShowAddForm(false)} style={styles.cancelBtn}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: '#94a3b8' }}>Loading...</p>
      ) : canRun && items.length === 0 && !error ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: 32 }}>No {itemName || 'items'} yet.</p>
      ) : (
        <div style={styles.list}>
          {items.map(item => {
            const isEditing = editingId === item._id;
            const isBusy = busyId === item._id;
            return (
              <div key={item._id} style={styles.card}>
                {canWrite && isEditing ? (
                  <>
                    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {fields.map(f => (
                        <div key={f.name} style={{ minWidth: 140 }}>
                          <label style={styles.labelSmall}>{f.placeholder || f.name}</label>
                          {fieldInput(f, editData[f.name], v => setEditData({ ...editData, [f.name]: v }))}
                        </div>
                      ))}
                    </div>
                    <button onClick={() => submitEdit(item._id)} disabled={isBusy} style={styles.iconBtnGood}><Check size={16} /></button>
                    <button onClick={cancelEdit} disabled={isBusy} style={styles.iconBtnNeutral}><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      {(specs.cardDisplayFields?.length ? specs.cardDisplayFields : fields.map(f => f.name)).map(fname => (
                        <div key={fname}>
                          <div style={styles.fieldLabel}>{fname}</div>
                          <div style={styles.fieldValue}>{String(item[fname] ?? '-')}</div>
                        </div>
                      ))}
                    </div>
                    {canWrite && (
                      <>
                        <button onClick={() => startEdit(item)} disabled={isBusy} style={styles.iconBtnNeutral}><Pencil size={15} /></button>
                        <button onClick={() => doDelete(item._id)} disabled={isBusy} style={styles.iconBtnBad}><Trash2 size={15} /></button>
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '32px', minHeight: '100vh', background: `linear-gradient(135deg,${rgba(theme.colors.primaryRgb, 0.05)},${rgba(theme.colors.accentRgb, 0.05)})` },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { color: '#e2e8f0', fontSize: '28px', fontWeight: '700', margin: 0 },
  sub: { color: theme.colors.textOnPageBackground, fontSize: '14px', marginTop: '4px' },
  addBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'linear-gradient(135deg,#6C63FF,#3b82f6)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  notice: { padding: '16px', borderRadius: 8, background: 'rgba(100,116,139,0.15)', color: '#94a3b8', marginBottom: 16 },
  errorBox: { padding: '16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', marginBottom: 16 },
  linkBtn: { marginTop: 8, background: 'transparent', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 13 },
  formCard: { background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: 20, marginBottom: 20 },
  formRow: { marginBottom: 12 },
  label: { display: 'block', color: '#94a3b8', fontSize: 13, marginBottom: 4 },
  labelSmall: { display: 'block', color: '#64748b', fontSize: 11, marginBottom: 2 },
  input: { width: '100%', padding: '8px 12px', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#e2e8f0', fontSize: 14, outline: 'none' },
  saveBtn: { padding: '10px 20px', background: 'linear-gradient(135deg,#6C63FF,#3b82f6)', border: 'none', borderRadius: 8, color: 'white', fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', background: 'rgba(100,116,139,0.2)', border: 'none', borderRadius: 8, color: '#94a3b8', cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 12 },
  fieldLabel: { color: '#64748b', fontSize: 11, textTransform: 'uppercase' },
  fieldValue: { color: '#e2e8f0', fontSize: 14 },
  iconBtnNeutral: { background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#3b82f6', padding: 6, display: 'flex', alignItems: 'center', cursor: 'pointer' },
  iconBtnGood: { background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', padding: 6, display: 'flex', alignItems: 'center', cursor: 'pointer' },
  iconBtnBad: { background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', padding: 6, display: 'flex', alignItems: 'center', cursor: 'pointer' },
};
